import { announce } from '@react-aria/live-announcer';
import { useUpdateEffect } from '@react-aria/utils';
import { useState } from 'react';
import { IconChevronLeft, IconChevronRight } from '../../../icons/index.js';
import type { IconButtonProps } from '../../button/icon-button.js';
import type { ViewGridState } from './types.js';

interface UseViewHeader {
  prevButtonProps: IconButtonProps;
  nextButtonProps: IconButtonProps;
}

/** @internal */
export function useViewHeader(state: ViewGridState): UseViewHeader {
  const { isFocused, header } = state;

  // Announce when the section changes
  useUpdateEffect(() => {
    // only when pressing the Previous or Next button
    if (!isFocused) {
      announce(header);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [header]);

  // If the next or previous buttons become disabled while they are focused, move focus to the grid.
  const [previousFocused, setPreviousFocused] = useState(false);
  const previousDisabled = state.isDisabled || state.isPreviousSectionInvalid();
  if (previousDisabled && previousFocused) {
    setPreviousFocused(false);
    state.setIsFocused(true);
  }

  const [nextFocused, setNextFocused] = useState(false);
  const nextDisabled = state.isDisabled || state.isNextSectionInvalid();
  if (nextDisabled && nextFocused) {
    setNextFocused(false);
    state.setIsFocused(true);
  }

  return {
    prevButtonProps: {
      onPress: () => state.focusPreviousSection(),
      'aria-label': state.previousLabel,
      icon: <IconChevronLeft />,
      variant: 'tertiary',
      size: 'small',
      isDisabled: previousDisabled,
      // @ts-expect-error our buttons don't expose `onFocusChange`, but it gets passed to React Aria anyway
      onFocusChange: setPreviousFocused,
    },
    nextButtonProps: {
      onPress: () => state.focusNextSection(),
      'aria-label': state.nextLabel,
      icon: <IconChevronRight />,
      variant: 'tertiary',
      size: 'small',
      isDisabled: nextDisabled,
      // @ts-expect-error our buttons don't expose `onFocusChange`, but it gets passed to React Aria anyway
      onFocusChange: setNextFocused,
    },
  };
}
